import { useDeviceSelector } from 'js/store/hooks';
import { compose, identity, omit, prop } from 'ramda';
import { mapProps, withHandlers, withProps } from 'recompose';

import * as actions from './store/actions';
import { withDynamicReducer, useDynamicReducerDispatch, useDynamicReducerSelector } from './store';

export default compose(
    withDynamicReducer(),
    withProps(() => ({
        isTouchDevice: useDeviceSelector(prop('isTouchDevice')),
        swipe: useDynamicReducerSelector(identity),
        swipeDispatch: useDynamicReducerDispatch()
    })),
    withHandlers({
        handleBodyEvents: ({ isTouchDevice }) => (action, move, end, click) => {
            if (isTouchDevice) {
                document.body[`${action}EventListener`]('touchmove', move);
                document.body[`${action}EventListener`]('touchcancel', end);
                document.body[`${action}EventListener`]('touchend', end);
                document.body[`${action}EventListener`]('tap', click);
            } else {
                document.body[`${action}EventListener`]('mousemove', move);
                document.body[`${action}EventListener`]('mouseup', end);
                document.body[`${action}EventListener`]('mouseleave', end);
                document.body[`${action}EventListener`]('click', click);
            }
        }
    }),
    withHandlers((props) => {
        const { swipeAngleX } = props.options || {};
        const swipeMove = ({ swipe, swipeDispatch }) => (event) => {
            if (!swipe.started) {
                return;
            }

            const { changedTouches, pageX, pageY } = event;
            const posX = changedTouches ? changedTouches[0].pageX : pageX;
            const posY = changedTouches ? changedTouches[0].pageY : pageY;
            const deltaX = swipe.startX - posX;
            const deltaY = swipe.startY - posY;
            const angleX = Math.atan(Math.abs(deltaY) / Math.abs(deltaX)) * 180 / Math.PI;

            if (!swipeAngleX || angleX <= swipeAngleX) {
                swipeDispatch(actions.updateData({ deltaX, deltaY }));
            }
        };

        const swipeEnd = ({ swipe, swipeDispatch }) => () => {
            if (!swipe.started) {
                return;
            }
            swipeDispatch(actions.resetToDefault());
        };

        const preventClick = ({ swipe, handleBodyEvents }) => (event) => {
            (swipe.deltaX || swipe.deltaY) && event.stopPropagation();
            handleBodyEvents('remove', swipeMove, swipeEnd, preventClick);
        };

        return {
            swipeMove,
            swipeEnd,
            preventClick
        };
    }),
    withHandlers({
        swipeStart: ({
            isSwipeEnabled = true,
            handleBodyEvents,
            swipeDispatch,
            swipeMove,
            swipeEnd,
            preventClick
        }) => ({ changedTouches, pageX, pageY }) => {
            if (!isSwipeEnabled) {
                return;
            }

            handleBodyEvents('add', swipeMove, swipeEnd, preventClick);
            swipeDispatch(actions.updateData({
                started: true,
                startX: changedTouches ? changedTouches[0].pageX : pageX,
                startY: changedTouches ? changedTouches[0].pageY : pageY,
                deltaX: 0,
                deltaY: 0
            }));
        }
    }),
    mapProps(({ swipe, enableAxisX = true, enableAxisY = false, ...other }) => ({
        ...omit([
            'isTouchDevice',
            'swipeDispatch',
            'handleBodyEvents',
            'swipeMove',
            'swipeEnd',
            'preventClick'
        ])(other),
        swipeStarted: swipe.started,
        ...enableAxisX && { swipeDeltaX: swipe.deltaX },
        ...enableAxisY && { swipeDeltaY: swipe.deltaY }
    }))
);
